import { useEffect, useRef } from 'react';

/**
 * React hook that sets CSS variables for viewport height management.
 * - `--vh-actual`: Current visual viewport height (updates dynamically)
 * - `--vh-frozen`: Initial viewport height, maintained when keyboard opens
 * - `--viewport-offset-top`: Visual viewport offset from top (for positioning fixed elements)
 *
 * This helps maintain consistent heights when the mobile keyboard appears.
 */
export const useVisualViewportHeight = () => {
    const initialHeightRef = useRef<number | null>(null);
    const initialWidthRef = useRef<number | null>(null);
    const KEYBOARD_THRESHOLD_RATIO = 0.75; // Consider keyboard open if viewport < 75% of initial

    useEffect(() => {
        document.documentElement.removeAttribute('data-keyboard-open');

        if (!window.visualViewport) {
            // Fallback for browsers without visualViewport
            const fallbackHeight = window.innerHeight;
            document.documentElement.style.setProperty(
                '--vh-actual',
                `${fallbackHeight}px`
            );
            document.documentElement.style.setProperty(
                '--vh-frozen',
                `${fallbackHeight}px`
            );
            document.documentElement.style.setProperty(
                '--viewport-offset-top',
                '0px'
            );
            return;
        }

        const ORIENTATION_CHANGE_WIDTH_PX = 50;

        const setCssVars = (height: number, offsetTop: number) => {
            const doc = document.documentElement.style;
            doc.setProperty('--vh-actual', `${height}px`);
            doc.setProperty('--viewport-offset-top', `${offsetTop}px`);
        };

        const setKeyboardOpen = (open: boolean) => {
            if (open) {
                document.documentElement.setAttribute(
                    'data-keyboard-open',
                    'true'
                );
            } else {
                document.documentElement.removeAttribute('data-keyboard-open');
            }
        };

        const updateBaseline = (height: number, width: number) => {
            initialHeightRef.current = height;
            initialWidthRef.current = width;
            document.documentElement.style.setProperty(
                '--vh-frozen',
                `${height}px`
            );
        };

        const isOrientationChange = (currentWidth: number) =>
            Math.abs(currentWidth - (initialWidthRef.current ?? 0)) >
            ORIENTATION_CHANGE_WIDTH_PX;

        const isKeyboardOpen = (currentHeight: number) => {
            const baseline = initialHeightRef.current ?? currentHeight;
            return currentHeight < baseline * KEYBOARD_THRESHOLD_RATIO;
        };

        const resizeHandler = () => {
            const vv = window.visualViewport;
            if (!vv) return;

            const { height: currentHeight, width: currentWidth } = vv;
            const offsetTop = vv.offsetTop;
            const initialized = initialHeightRef.current !== null;

            if (!initialized) {
                updateBaseline(currentHeight, currentWidth);
            } else if (isOrientationChange(currentWidth)) {
                setKeyboardOpen(false);
                updateBaseline(currentHeight, currentWidth);
            } else {
                if (isKeyboardOpen(currentHeight)) {
                    setKeyboardOpen(true);
                } else {
                    setKeyboardOpen(false);
                    updateBaseline(currentHeight, currentWidth);
                }
            }

            setCssVars(currentHeight, offsetTop);
        };

        const scrollHandler = () => {
            const vv = window.visualViewport;
            if (vv) {
                document.documentElement.style.setProperty(
                    '--viewport-offset-top',
                    `${vv.offsetTop}px`
                );
            }
        };

        window.visualViewport.addEventListener('resize', resizeHandler);
        window.visualViewport.addEventListener('scroll', scrollHandler);
        resizeHandler(); // Call on initial load

        return () => {
            document.documentElement.removeAttribute('data-keyboard-open');
            window.visualViewport?.removeEventListener('resize', resizeHandler);
            window.visualViewport?.removeEventListener('scroll', scrollHandler);
        };
    }, []);
};
